import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowRight, RotateCcw, Flower2, Gift, Check, Share2, Printer } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { GardienBadge } from "@/components/GardienBadge";
import { GARDIENS, gardienById, PRODUCT_GARDIENS } from "@/data/gardiens";
import { PILL_BTN } from "@/lib/pill";
import { downloadGardienCard } from "@/lib/gardienCard";
import { api } from "@/lib/api";

// Chaque réponse attribue des points à un ou deux Gardiens (barème : memory/quiz_points.txt)
const QUESTIONS = [
  {
    q: "Qu'est-ce qui le ou la fait vibrer le plus ?",
    options: [
      { label: "Se plonger dans les photos d'antan", points: { eloise: 3, adele: 2 } },
      { label: "Raconter ses histoires du temps jadis", points: { arsene: 3, silas: 2 } },
      { label: "Jardiner, bricoler, prendre son temps", points: { madeleine: 3, theodore: 2 } },
      { label: "La musique et les chansons d'autrefois", points: { gaston: 3, aurelien: 2 } },
    ],
  },
  {
    q: "Ce qui le ou la rassure le plus…",
    options: [
      { label: "Une photo de ceux qu'il ou elle aime", points: { "rose-auguste": 3, eloise: 2 } },
      { label: "Une lettre ou la visite d'un ami", points: { lucien: 3, emile: 2 } },
      { label: "Le calme, une lumière douce", points: { josephine: 3, victor: 2 } },
      { label: "Une histoire qu'on lui lit", points: { marianne: 3, celestine: 2 } },
    ],
  },
  {
    q: "Son tempérament, plutôt…",
    options: [
      { label: "Rêveur ou rêveuse", points: { celestine: 3, aurelien: 2 } },
      { label: "Joyeux, taquin, aime faire rire", points: { leontine: 3, gaston: 2 } },
      { label: "Posé et patient", points: { theodore: 3, marianne: 2 } },
      { label: "Tourné vers les autres", points: { silas: 3, lucien: 2 } },
    ],
  },
  {
    q: "Son plus beau souvenir évoque souvent…",
    options: [
      { label: "Un amour de toujours", points: { "rose-auguste": 3, josephine: 2 } },
      { label: "Des fêtes pleines de chansons", points: { gaston: 3, leontine: 2 } },
      { label: "Des voyages, des mondes imaginaires", points: { aurelien: 3, agathe: 2 } },
      { label: "La maison et les siens", points: { adele: 3, theodore: 2 } },
    ],
  },
  {
    q: "Ce qui lui ferait plaisir aujourd'hui…",
    options: [
      { label: "Transmettre une histoire aux petits-enfants", points: { agathe: 3, arsene: 2 } },
      { label: "Cultiver quelque chose de ses mains", points: { victor: 3, madeleine: 2 } },
      { label: "Garder le sourire quoi qu'il arrive", points: { emile: 3, leontine: 2 } },
      { label: "Partager un goûter entre amis", points: { lucien: 3, "rose-auguste": 2 } },
    ],
  },
];

const QuizGardiens = () => {
  const [step, setStep] = useState(0); // 0..4 = questions, 5 = résultat
  const [scores, setScores] = useState({});
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Vue partagée : /quiz-gardien?g=<id> affiche directement le Gardien reçu
  const shared = gardienById(searchParams.get("g")) || null;
  // Choix direct : sans passer par les questions
  const [directPick, setDirectPick] = useState(null);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const answer = (points) => {
    const next = { ...scores };
    Object.entries(points).forEach(([id, pts]) => { next[id] = (next[id] || 0) + pts; });
    setScores(next);
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const result = useMemo(() => {
    if (shared) return shared;
    if (directPick) return directPick;
    if (step < QUESTIONS.length) return null;
    const best = GARDIENS.reduce((a, g) => ((scores[g.id] || 0) > (scores[a.id] || 0) ? g : a), GARDIENS[0]);
    return best;
  }, [step, scores, shared, directPick]);

  const suggestedProducts = useMemo(() => {
    if (!result) return [];
    const mapped = products.filter((p) => (p.gardien || PRODUCT_GARDIENS[p.slug]) === result.id);
    const inCat = products.filter((p) => p.category === result.category && !mapped.includes(p));
    const rest = products.filter((p) => !mapped.includes(p) && !inCat.includes(p));
    return [...mapped, ...inCat, ...rest].slice(0, 2);
  }, [result, products]);

  const restart = () => {
    setStep(0);
    setScores({});
    setDirectPick(null);
    if (shared) setSearchParams({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const share = async () => {
    const url = `${window.location.origin}/quiz-gardien?g=${result.id}`;
    const text = `${result.name}, ${result.title} — « ${result.quote} »`;
    if (navigator.share) {
      try { await navigator.share({ title: `Son Gardien : ${result.name}`, text, url }); } catch { /* partage annulé */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Lien copié — envoyez-le à votre famille !");
      } catch {
        toast.error("Copie impossible, copiez l'adresse de la page.");
      }
    }
  };

  const [printing, setPrinting] = useState(false);
  const printCard = async () => {
    setPrinting(true);
    try { await downloadGardienCard(result); } catch { toast.error("La carte n'a pas pu être créée."); }
    setPrinting(false);
  };

  return (
    <div className="pt-32 pb-24">
      <section className="max-w-3xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-warm mb-5">Le quiz des Gardiens</p>
          <h1 className="font-serif-display italic text-5xl sm:text-6xl font-light leading-tight">Trouver son Gardien</h1>
          <p className="mt-5 text-lg text-foreground/70 leading-relaxed">
            Pensez à la personne que vous accompagnez, et répondez comme elle le ferait.
          </p>
        </Reveal>
        <div className="mt-8">
          <GardienBadge gardienId="agathe" label="Gardienne de ce quiz" />
        </div>

        {!result ? (
          <div className="mt-12" data-testid="quiz-step">
            {/* Progression */}
            <div className="flex items-center gap-3 mb-10">
              {QUESTIONS.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full flex-1 transition-colors duration-500 ${i < step ? "bg-warm" : i === step ? "bg-warm/50" : "bg-border"}`} />
              ))}
              <span className="text-xs text-muted-foreground ml-2 whitespace-nowrap">{step + 1} / {QUESTIONS.length}</span>
            </div>
            <Reveal key={step}>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-snug" data-testid="quiz-question">{QUESTIONS[step].q}</h2>
              <div className="mt-8 space-y-4">
                {QUESTIONS[step].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => answer(opt.points)}
                    data-testid={`quiz-answer-${i}`}
                    className="w-full text-left bg-card rounded-2xl border border-warm/25 px-7 py-5 text-base sm:text-lg hover:border-warm hover:bg-warm/10 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Reveal>
            {/* Choix direct, sans le quiz */}
            <div className="mt-14 text-center" data-testid="quiz-direct-pick">
              <p className="text-xs uppercase tracking-[0.25em] text-warm mb-2">Vous le connaissez déjà ?</p>
              <p className="font-serif-display italic text-2xl text-foreground/80 mb-7">Choisissez directement votre Gardien</p>
              <div className="flex flex-wrap justify-center gap-x-5 gap-y-6 max-w-4xl mx-auto">
                {GARDIENS.map((g) => (
                  <button key={g.id} onClick={() => { setDirectPick(g); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    data-testid={`quiz-pick-${g.id}`}
                    className="group flex flex-col items-center w-20 hover:-translate-y-1 transition-transform duration-300">
                    <img src={g.image} alt={g.name} className="w-16 h-16 rounded-full object-cover object-top group-hover:scale-110 transition-transform duration-500" style={{ boxShadow: `0 0 0 2px ${g.color}55` }} />
                    <span className="mt-2 text-xs font-medium leading-tight group-hover:text-primary transition-colors">{g.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Reveal>
            <div className="mt-12" data-testid="quiz-result">
              {shared && (
                <p data-testid="quiz-shared-banner" className="mb-6 text-center text-sm text-warm bg-warm/10 border border-warm/30 rounded-full px-6 py-3">
                  Une famille vous partage ce Gardien — découvrez le vôtre ensuite !
                </p>
              )}
              <div className="bg-card rounded-[2rem] border border-warm/30 p-8 sm:p-12 text-center grain overflow-hidden">
                <p className="text-sm uppercase tracking-[0.25em] text-warm">Son Gardien est…</p>
                <div className="relative mx-auto w-48 h-48 mt-8">
                  <div className="absolute inset-0 rounded-full animate-pulse" style={{ boxShadow: `0 0 0 5px ${result.color}44, 0 0 60px ${result.color}55` }} />
                  <img src={result.image} alt={result.name} className="w-48 h-48 rounded-full object-cover object-top" data-testid="quiz-result-image" />
                </div>
                <h2 className="font-serif-display text-4xl sm:text-5xl mt-8" data-testid="quiz-result-name">{result.name}</h2>
                <p className="text-muted-foreground mt-1">{result.title}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.25em]" style={{ color: result.color }} data-testid="quiz-result-value">{result.value}</p>
                <p className="font-serif-display italic text-2xl text-foreground/80 mt-6 max-w-lg mx-auto leading-relaxed">« {result.quote} »</p>
                <Link to={`/gardiens/${result.id}`} data-testid="quiz-gardien-page" className="inline-block mt-6 text-sm text-warm hover:text-primary transition-colors underline underline-offset-4">
                  Découvrir la page de {result.name} →
                </Link>
                {result.familiar && (
                  <div data-testid="quiz-result-familiar" className="mt-8 inline-flex items-center gap-4 bg-warm/10 border border-warm/30 rounded-2xl px-6 py-4">
                    <img src={result.familiar.image} alt={result.familiar.name} className="w-14 h-14 rounded-full object-cover" style={{ boxShadow: `0 0 0 3px ${result.color}44` }} />
                    <span className="text-left text-sm text-foreground/80 leading-snug">
                      …accompagné de son fidèle compagnon<br />
                      <strong className="font-medium text-foreground">{result.familiar.name}</strong>, {result.familiar.title}
                    </span>
                  </div>
                )}
              </div>

              {/* ACTIVITÉS */}
              <div className="mt-8 bg-card rounded-[2rem] border border-border/50 p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <Flower2 className="w-6 h-6 text-warm" strokeWidth={1.5} />
                  <h3 className="font-serif-display text-2xl">Activités dans son univers</h3>
                </div>
                <ul className="space-y-4">
                  {result.activities.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-foreground/80 leading-relaxed" data-testid={`quiz-activity-${i}`}>
                      <Check className="w-5 h-5 text-warm shrink-0 mt-0.5" /> {a}
                    </li>
                  ))}
                </ul>
              </div>

              {/* OBJETS ADAPTÉS */}
              {suggestedProducts.length > 0 && (
                <div className="mt-8 bg-card rounded-[2rem] border border-border/50 p-8 sm:p-10">
                  <div className="flex items-center gap-3 mb-6">
                    <Gift className="w-6 h-6 text-warm" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-2xl">Objets qui lui iront bien</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {suggestedProducts.map((p) => (
                      <Link to="/boutique" key={p.slug} data-testid={`quiz-product-${p.slug}`} className="group flex gap-4 items-center bg-background rounded-2xl border border-border/50 p-4 hover:-translate-y-1 hover:shadow-[0_16px_30px_rgb(0,0,0,0.08)] transition-all duration-300">
                        <img src={p.image} alt={p.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                        <div>
                          <p className="font-medium leading-snug group-hover:text-primary transition-colors">{p.name}</p>
                          <p className="font-serif-display text-xl mt-1">{p.price.toFixed(0)} €</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <button onClick={share} data-testid="quiz-share-btn" className="inline-flex items-center gap-2 rounded-full bg-warm text-warm-foreground px-8 py-4 hover:scale-105 transition-transform duration-300">
                  <Share2 className="w-4 h-4" /> Partager mon Gardien
                </button>
                <button onClick={printCard} disabled={printing} data-testid="quiz-print-btn" className="inline-flex items-center gap-2 rounded-full border border-warm/50 text-warm px-8 py-4 hover:bg-warm/10 transition-colors disabled:opacity-60">
                  <Printer className="w-4 h-4" /> {printing ? "Création de la carte…" : "Imprimer la carte"}
                </button>
                <Link to="/boutique" data-testid="quiz-boutique-btn" className={PILL_BTN}>
                  Voir la boutique <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button onClick={restart} data-testid="quiz-restart-btn" className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-8 py-4 hover:bg-accent transition-colors">
                  <RotateCcw className="w-4 h-4" /> {shared ? "Faire le quiz à mon tour" : directPick ? "Choisir autrement" : "Refaire le quiz"}
                </button>
              </div>
            </div>
          </Reveal>
        )}

        {step > 0 && step < QUESTIONS.length && (
          <p className="mt-10 text-center">
            <Link to="/gardiens" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="quiz-back-gallery">
              ← Découvrir tous les Gardiens
            </Link>
          </p>
        )}
      </section>
    </div>
  );
};

export default QuizGardiens;
